import React from "react";
import { Link, useParams } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  Stack,
  IconButton,
  Divider,
  alpha,
  useTheme,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  Person as PersonIcon,
  ArrowForwardIos as ArrowIcon,
  Info as InfoIcon,
  ContactPhone as ContactIcon,
  WarningAmber as AllergyIcon,
  Inventory as InventoryIcon,
  Payments as PaymentIcon,
  Emergency as EmergencyIcon,
  Description as DocumentIcon,
} from "@mui/icons-material";
import { useQuery } from "react-query";
import { getPersonalDetailsSummary } from "../../../api/services/Client";

const PersonalDetails = () => {
  const theme = useTheme();
  const { id: clientID } = useParams();

  const { data: summary, isLoading, error } = useQuery(
    ["personalDetailsSummary", clientID],
    () => getPersonalDetailsSummary(clientID),
    {
      enabled: !!clientID,
      staleTime: 5 * 60 * 1000,
    }
  );

  const infoList = [
    {
      label: "About me",
      value: summary?.about_me || 0,
      icon: <InfoIcon sx={{ fontSize: 14, color: theme.palette.info.main }} />,
      iconBg: alpha(theme.palette.info.main, 0.08),
      link: `/personal-details/${clientID}#about-me`
    },
    {
      label: "Contacts",
      value: summary?.contacts?.total || 0,
      icon: <ContactIcon sx={{ fontSize: 14, color: theme.palette.success.main }} />,
      iconBg: alpha(theme.palette.success.main, 0.08),
      link: `/personal-details/${clientID}#contacts-family`
    },
    {
      label: "Allergies",
      value: summary?.allergies || 0,
      icon: <AllergyIcon sx={{ fontSize: 14, color: theme.palette.warning.main }} />,
      iconBg: alpha(theme.palette.warning.main, 0.08),
      link: `/personal-details/${clientID}#allergies`
    },
    {
      label: "Personal Inventory",
      value: summary?.inventory || 0,
      icon: <InventoryIcon sx={{ fontSize: 14, color: theme.palette.secondary.main }} />,
      iconBg: alpha(theme.palette.secondary.main, 0.08),
      link: `/personal-details/${clientID}#personal-inventory`
    },
    {
      label: "Funders",
      value: summary?.funders || 0,
      icon: <PaymentIcon sx={{ fontSize: 14, color: theme.palette.success.main }} />,
      iconBg: alpha(theme.palette.success.main, 0.08),
      link: `/personal-details/${clientID}#funding`
    },
    {
      label: "DNACPR",
      value: summary?.dnacpr || 0,
      icon: <EmergencyIcon sx={{ fontSize: 14, color: theme.palette.error.main }} />,
      iconBg: alpha(theme.palette.error.main, 0.08),
      link: `/personal-details/${clientID}#documents`
    },
    {
      label: "LPA",
      value: summary?.lpa || 0,
      icon: <PersonIcon sx={{ fontSize: 14, color: theme.palette.secondary.main }} />,
      iconBg: alpha(theme.palette.secondary.main, 0.08),
      link: `/personal-details/${clientID}#documents`
    },
    {
      label: "Documents",
      value: summary?.documents || 0,
      icon: <DocumentIcon sx={{ fontSize: 14, color: theme.palette.primary.main }} />,
      iconBg: alpha(theme.palette.primary.main, 0.08),
      link: `/personal-details/${clientID}#documents`
    },
  ];

  if (isLoading) {
    return (
      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
        }}
      >
        <CircularProgress size={32} />
      </Card>
    );
  }

  if (error) {
    return (
      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
          p: 2,
        }}
      >
        <Typography color="error" variant="body2">
          Failed to load personal details
        </Typography>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        borderRadius: "12px",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
        overflow: "hidden",
      }}
    >
      <CardHeader
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: "#FAFBFC",
          borderBottom: "1px solid #F2F4F7",
        }}
        title={
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "6px",
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PersonIcon sx={{ color: theme.palette.primary.main, fontSize: 16 }} />
              </Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: "#1A1A2E", fontSize: "0.85rem" }}>
                Personal Details
              </Typography>
              <Chip
                label={infoList.length}
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.55rem",
                  fontWeight: 500,
                  bgcolor: "#F2F4F7",
                  color: "#667085",
                }}
              />
            </Box>
            <IconButton
              component={Link}
              to={`/personal-details/${clientID}`}
              size="small"
              sx={{
                width: 28,
                height: 28,
                bgcolor: "#fff",
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                "&:hover": {
                  bgcolor: theme.palette.primary.main,
                  color: "#fff",
                },
                transition: "all 0.2s",
              }}
            >
              <ArrowIcon sx={{ fontSize: 9 }} />
            </IconButton>
          </Box>
        }
      />

      <Divider sx={{ opacity: 0.4 }} />

      <CardContent sx={{ p: 1.5 }}>
        <Stack spacing={0.75}>
          {infoList.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: 0.5,
                px: 0.5,
                borderRadius: "4px",
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                },
              }}
            >
              <Box
                component={Link}
                to={item.link}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  textDecoration: "none",
                  color: "inherit",
                  flex: 1,
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: "4px",
                    bgcolor: item.iconBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {item.icon}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#1A1A2E",
                    fontWeight: 500,
                    fontSize: "0.75rem",
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#667085",
                  fontSize: "0.7rem",
                  fontWeight: 500,
                }}
              >
                {item.value}
              </Typography>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default PersonalDetails;